import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = ({ isDarkMode }) => {
  const [data, setData] = useState(null);
  const [quizStats, setQuizStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(7);

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [dashboardRes, quizRes] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/dashboard?days=${period}`),
        axios.get(`${API_URL}/api/analytics/quiz-completion-rate?days=${period}`)
      ]);
      setData(dashboardRes.data);
      setQuizStats(quizRes.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const cardClass = `p-4 rounded-xl ${isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-slate-200'}`;
  const labelClass = `text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`;
  const valueClass = `text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`;
  const subValueClass = `text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`text-center py-12 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
        No analytics data available yet. Events will appear as users interact with the app.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Conversion Analytics
        </h3>
        <div className="flex gap-2">
          {[7, 30, 90].map((days) => (
            <button
              key={days}
              onClick={() => setPeriod(days)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === days
                  ? 'bg-purple-600 text-white'
                  : isDarkMode
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={cardClass}>
          <p className={labelClass}>Homepage Visits</p>
          <p className={valueClass}>{data.funnel.homepage_visits.toLocaleString()}</p>
        </div>
        <div className={cardClass}>
          <p className={labelClass}>Student CTAs</p>
          <p className={valueClass}>{data.funnel.student_cta_clicks.toLocaleString()}</p>
          <p className={subValueClass}>
            {data.conversion_rates.student.cta_to_signup}% → signup
          </p>
        </div>
        <div className={cardClass}>
          <p className={labelClass}>Teacher CTAs</p>
          <p className={valueClass}>{data.funnel.teacher_cta_clicks.toLocaleString()}</p>
          <p className={subValueClass}>
            {data.conversion_rates.teacher.cta_to_signup}% → signup
          </p>
        </div>
        <div className={cardClass}>
          <p className={labelClass}>Trials Started</p>
          <p className={valueClass}>{data.funnel.trials_started.toLocaleString()}</p>
        </div>
      </div>

      {/* Quiz Funnel */}
      <div className={`${cardClass} space-y-4`}>
        <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quiz Funnel</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <p className={labelClass}>Started</p>
            <p className={valueClass}>{data.funnel.quizzes_started.toLocaleString()}</p>
          </div>
          <div>
            <p className={labelClass}>Completed</p>
            <p className={valueClass}>{data.funnel.quizzes_completed.toLocaleString()}</p>
          </div>
          <div>
            <p className={labelClass}>Completion Rate</p>
            <p className={`${valueClass} ${data.funnel.quiz_completion_rate >= 50 ? 'text-green-500' : 'text-amber-500'}`}>
              {data.funnel.quiz_completion_rate}%
            </p>
          </div>
          {quizStats && (
            <>
              <div>
                <p className={labelClass}>Failed</p>
                <p className={`${valueClass} text-red-500`}>{quizStats.quizzes_failed.toLocaleString()}</p>
              </div>
              <div>
                <p className={labelClass}>Abandoned</p>
                <p className={`${valueClass} text-amber-500`}>{quizStats.quizzes_abandoned.toLocaleString()}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Quiz Progress Funnel (Abandonment Analysis) */}
      {data.quiz_progress_funnel && (
        <div className={`${cardClass} space-y-4`}>
          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Quiz Progress Funnel</h4>
          <div className="space-y-2">
            {[
              { label: 'Material Selected', value: data.quiz_progress_funnel.material_selected },
              { label: 'Material Uploaded', value: data.quiz_progress_funnel.material_uploaded },
              { label: 'Generation Started', value: data.quiz_progress_funnel.generation_started },
              { label: 'Generated Success', value: data.quiz_progress_funnel.generated_success },
              { label: 'Quiz Opened', value: data.quiz_progress_funnel.quiz_opened },
              { label: 'First Answer', value: data.quiz_progress_funnel.first_answer },
              { label: '25% Progress', value: data.quiz_progress_funnel.progress_25 },
              { label: '50% Progress', value: data.quiz_progress_funnel.progress_50 },
              { label: '75% Progress', value: data.quiz_progress_funnel.progress_75 },
              { label: 'Completed', value: data.quiz_progress_funnel.completed },
            ].map((step, i, arr) => {
              const maxVal = arr[0].value || 1;
              const percentage = maxVal > 0 ? Math.round((step.value / maxVal) * 100) : 0;
              return (
                <div key={step.label} className="flex items-center gap-3">
                  <div className={`w-28 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>{step.label}</div>
                  <div className="flex-1">
                    <div className={`h-4 rounded ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                      <div 
                        className={`h-4 rounded ${i === arr.length - 1 ? 'bg-green-500' : 'bg-purple-500'}`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                  </div>
                  <div className={`w-12 text-right text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                    {step.value}
                  </div>
                </div>
              );
            })}
            {data.quiz_progress_funnel.abandoned > 0 && (
              <div className="flex items-center gap-3 pt-2 border-t border-slate-200 dark:border-slate-700">
                <div className={`w-28 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Abandoned</div>
                <div className={`text-sm font-medium text-amber-500`}>
                  {data.quiz_progress_funnel.abandoned} users left before completion
                </div>
              </div>
            )}
            {data.quiz_progress_funnel.generated_failed > 0 && (
              <div className="flex items-center gap-3">
                <div className={`w-28 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>Gen. Failed</div>
                <div className={`text-sm font-medium text-red-500`}>
                  {data.quiz_progress_funnel.generated_failed} generation failures
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signup & Payment Funnel */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Student Funnel */}
        <div className={`${cardClass} space-y-3`}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-sky-500"></div>
            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Student Funnel</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={subValueClass}>CTA Clicks</span>
              <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{data.funnel.student_cta_clicks}</span>
            </div>
            <div className="flex justify-between">
              <span className={subValueClass}>Signups</span>
              <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{data.funnel.student_signups}</span>
            </div>
            <div className="flex justify-between">
              <span className={subValueClass}>CTA → Signup</span>
              <span className={`font-bold ${data.conversion_rates.student.cta_to_signup >= 10 ? 'text-green-500' : 'text-amber-500'}`}>
                {data.conversion_rates.student.cta_to_signup}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className={subValueClass}>Signup → Paid</span>
              <span className={`font-bold ${data.conversion_rates.student.signup_to_paid >= 5 ? 'text-green-500' : 'text-amber-500'}`}>
                {data.conversion_rates.student.signup_to_paid}%
              </span>
            </div>
          </div>
        </div>

        {/* Teacher Funnel */}
        <div className={`${cardClass} space-y-3`}>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Teacher Funnel</h4>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className={subValueClass}>CTA Clicks</span>
              <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{data.funnel.teacher_cta_clicks}</span>
            </div>
            <div className="flex justify-between">
              <span className={subValueClass}>Signups</span>
              <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{data.funnel.teacher_signups}</span>
            </div>
            <div className="flex justify-between">
              <span className={subValueClass}>CTA → Signup</span>
              <span className={`font-bold ${data.conversion_rates.teacher.cta_to_signup >= 10 ? 'text-green-500' : 'text-amber-500'}`}>
                {data.conversion_rates.teacher.cta_to_signup}%
              </span>
            </div>
            <div className="flex justify-between">
              <span className={subValueClass}>Signup → Paid</span>
              <span className={`font-bold ${data.conversion_rates.teacher.signup_to_paid >= 5 ? 'text-green-500' : 'text-amber-500'}`}>
                {data.conversion_rates.teacher.signup_to_paid}%
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Pricing & Payment */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={cardClass}>
          <p className={labelClass}>Pricing Views</p>
          <p className={valueClass}>{data.funnel.pricing_views.toLocaleString()}</p>
        </div>
        <div className={cardClass}>
          <p className={labelClass}>Checkout Starts</p>
          <p className={valueClass}>{data.funnel.checkout_starts.toLocaleString()}</p>
        </div>
        <div className={cardClass}>
          <p className={labelClass}>Payments</p>
          <p className={`${valueClass} text-green-500`}>{data.funnel.payments_completed.toLocaleString()}</p>
        </div>
        <div className={cardClass}>
          <p className={labelClass}>Returning Users</p>
          <p className={valueClass}>{data.engagement.returning_users.toLocaleString()}</p>
        </div>
      </div>

      {/* Engagement */}
      <div className={`${cardClass} space-y-4`}>
        <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Engagement Events</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className={labelClass}>Practice Mistakes</p>
            <p className={valueClass}>{data.engagement.practice_mistakes_opened}</p>
            <p className={subValueClass}>{data.engagement.practice_mistakes_completed} completed</p>
          </div>
          <div>
            <p className={labelClass}>AI Solver Used</p>
            <p className={valueClass}>{data.engagement.ai_solver_used}</p>
          </div>
          <div>
            <p className={labelClass}>Quizzes Shared</p>
            <p className={valueClass}>{data.engagement.quizzes_shared}</p>
          </div>
          <div>
            <p className={labelClass}>PDFs Exported</p>
            <p className={valueClass}>{data.engagement.pdfs_exported}</p>
          </div>
          <div>
            <p className={labelClass}>Second Quizzes</p>
            <p className={valueClass}>{data.engagement.second_quizzes}</p>
          </div>
          <div>
            <p className={labelClass}>Classes Created</p>
            <p className={valueClass}>{data.engagement.classes_created}</p>
          </div>
          <div>
            <p className={labelClass}>Assignments</p>
            <p className={valueClass}>{data.engagement.assignments_created}</p>
          </div>
        </div>
      </div>

      {/* Traffic Sources */}
      {Object.keys(data.traffic_sources).length > 0 && (
        <div className={`${cardClass} space-y-4`}>
          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Top Traffic Sources</h4>
          <div className="space-y-2">
            {Object.entries(data.traffic_sources).map(([source, count]) => (
              <div key={source} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between mb-1">
                    <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>{source}</span>
                    <span className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>{count}</span>
                  </div>
                  <div className={`h-2 rounded-full ${isDarkMode ? 'bg-slate-700' : 'bg-slate-200'}`}>
                    <div 
                      className="h-2 rounded-full bg-purple-500"
                      style={{ width: `${(count / Math.max(...Object.values(data.traffic_sources))) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Chart */}
      {data.daily_breakdown && data.daily_breakdown.length > 0 && (
        <div className={`${cardClass} space-y-4`}>
          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Daily Trend</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={isDarkMode ? 'text-slate-400' : 'text-slate-500'}>
                  <th className="text-left py-2">Date</th>
                  <th className="text-right py-2">Visits</th>
                  <th className="text-right py-2">CTAs</th>
                  <th className="text-right py-2">Quizzes</th>
                  <th className="text-right py-2">Signups</th>
                  <th className="text-right py-2">Payments</th>
                </tr>
              </thead>
              <tbody>
                {data.daily_breakdown.map((day) => (
                  <tr key={day.date} className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'}`}>
                    <td className={`py-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>{day.date}</td>
                    <td className="text-right">{day.homepage_visits}</td>
                    <td className="text-right">{day.cta_clicks}</td>
                    <td className="text-right">{day.quizzes_started}</td>
                    <td className="text-right">{day.signups}</td>
                    <td className="text-right text-green-500">{day.payments}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* PostHog Status */}
      <div className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
        {process.env.REACT_APP_POSTHOG_API_KEY 
          ? '✓ PostHog connected' 
          : '○ PostHog not configured - Add REACT_APP_POSTHOG_API_KEY to enable'}
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
